import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Select, MenuItem, FormControl, FormControlLabel, InputLabel, TextField, IconButton, Link, Box, Typography, Table, TableHead, TableBody, TableRow, TableCell, Radio, RadioGroup, Alert, Divider } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { uploadInvoiceFile, updateReplenishmentPayment, deleteInvoiceFile, cancelReplenishment } from '../../services/ReplenishmentService';
import { getIcon } from '../../utils/IconHandler';
import { formatMoney } from '../../utils/currency';
import { formatDate } from '../../utils/DateUtils';

const PAID_STATUS = 1;

export const PAYMENT_METHOD_LABELS = {
  CASH: 'Efectivo',
  TRANSFER: 'Transferencia',
  CARD: 'Tarjeta'
};

const todayInputValue = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

// El backend espera fecha y hora; se usa la hora actual para que el orden del historial sea estable
const toPaymentDateTime = (dateValue) => `${dateValue}T${new Date().toTimeString().slice(0, 8)}`;

const EditReplenishmentModal = ({ open, onClose, replenishment, onSave }) => {

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoiceUrl, setInvoiceUrl] = useState(replenishment ? replenishment.invoiceFile : '');
  const [invoiceToDelete, setInvoiceToDelete] = useState(false);
  const [paymentType, setPaymentType] = useState('TOTAL');
  const [partialAmount, setPartialAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentDate, setPaymentDate] = useState(todayInputValue());
  const isPaymentManagedByB2B = Boolean(replenishment?.paymentManagedByB2B);
  const canCancelReplenishment = Boolean(replenishment) && !isPaymentManagedByB2B;
  const hasInvoiceChanges = Boolean(invoiceFile) || invoiceToDelete;
  const totalPrice = replenishment?.totalPrice ?? 0;
  const alreadyPaid = replenishment?.paidAmount ?? 0;
  const isFullyPaid = replenishment?.paymentStatus === PAID_STATUS || totalPrice - alreadyPaid <= 0.005;
  const remaining = isFullyPaid ? 0 : Math.max(totalPrice - alreadyPaid, 0);
  const canRegisterPayment = Boolean(replenishment) && !isPaymentManagedByB2B && !isFullyPaid;
  const isPartialPayment = paymentType === 'PARTIAL';
  const partialValue = parseFloat(partialAmount);
  const partialAmountError = isPartialPayment && partialAmount !== '' && (
    !(partialValue > 0)
      ? 'Ingresá un monto mayor a 0'
      : partialValue > remaining + 0.001
        ? `El monto no puede superar lo que falta pagar (${formatMoney(remaining, replenishment?.currency)})`
        : ''
  );
  const paymentTouched = canRegisterPayment && (Boolean(paymentMethod) || (isPartialPayment && partialAmount !== ''));
  const paymentValid = canRegisterPayment && Boolean(paymentMethod) && Boolean(paymentDate)
    && (!isPartialPayment || (partialValue > 0 && !partialAmountError));
  const payments = replenishment?.payments || [];
  // Reposiciones con pagos anteriores al historial: se muestra lo pagado como un único registro
  const historyRows = payments.length > 0 ? payments : (alreadyPaid > 0 ? [{
    id: 'legacy',
    amount: alreadyPaid,
    paymentDate: replenishment?.lastPaymentDate,
    paymentMethod: replenishment?.paymentMethod
  }] : []);
  const canSave = (hasInvoiceChanges || paymentValid) && !(paymentTouched && !paymentValid);
  const registeringPayment = paymentValid;
  const remainingAfter = paymentValid ? Math.max(remaining - (isPartialPayment ? partialValue : remaining), 0) : remaining;

  useEffect(() => {
    if (replenishment) {
      setInvoiceUrl(replenishment.invoiceFile);
      setInvoiceToDelete(false);
      setInvoiceFile(null);
      setPaymentType('TOTAL');
      setPartialAmount('');
      setPaymentMethod('');
      setPaymentDate(todayInputValue());
    }    
  }, [replenishment]);

  const handleFileChange = (event) => {
    setInvoiceFile(event.target.files[0]);
    setInvoiceUrl('');
    setInvoiceToDelete(false);
  };

  const markInvoiceForDeletion = () => {
    setInvoiceToDelete(true);
    setInvoiceUrl('');
  };

  const saveChanges = async () => {
    setLoading(true);
    try {
      // Comienza con la reposición actual para mantener los datos durante las actualizaciones
      let updatedReplenishment = replenishment;
  
      if (paymentValid) {
        // Cada pago queda en el historial; el backend lo suma a lo ya pagado
        updatedReplenishment = await updateReplenishmentPayment(replenishment.id, {
          paidAmount: isPartialPayment ? partialValue : remaining,
          isCompletePayment: !isPartialPayment,
          paymentMethod,
          paymentDate: toPaymentDateTime(paymentDate)
        });
      }
  
      // Subir nueva factura o eliminar la existente si corresponde.
      if (invoiceFile) {
        updatedReplenishment = await uploadInvoiceFile(invoiceFile, replenishment.id);
      } else if (invoiceToDelete) {
        updatedReplenishment = await deleteInvoiceFile(replenishment.id);
      }
  
      // Llamar a onSave con la reposición actualizada
      onSave(updatedReplenishment);
  
      onClose(); // Cierra el modal después de la actualización
    } catch (error) {
      console.error("Error updating replenishment details:", error);
      window.alert(
        error?.message ||
          t('error_updating_replenishment', {
            defaultValue: 'No se pudo actualizar la reposición'
          })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReplenishment = async () => {
    const confirmed = window.confirm(t('confirm_cancel_replenishment'));
    if (!confirmed) return;

    setLoading(true);
    try {
      const updatedReplenishment = await cancelReplenishment(replenishment.id);
      onSave(updatedReplenishment);
      onClose();
    } catch (error) {
      console.error('Error cancelling replenishment:', error);
      const backendMessage = error.response?.data?.error_message;
      window.alert(backendMessage || t('error_cancelling_replenishment'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Registrar pago</DialogTitle>
      <DialogContent>
        {replenishment && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 2, mt: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2">Total: <strong>{formatMoney(totalPrice, replenishment.currency)}</strong></Typography>
            <Typography variant="body2" color="success.main">Pagado: <strong>{formatMoney(isFullyPaid ? totalPrice : alreadyPaid, replenishment.currency)}</strong></Typography>
            <Typography variant="body2" color="error.main">Resta: <strong>{formatMoney(remainingAfter, replenishment.currency)}</strong></Typography>
          </Box>
        )}

        {isPaymentManagedByB2B && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('payment_status_managed_by_b2b', {
              defaultValue: 'El estado del pago de este pedido es administrado por el proveedor.'
            })}
          </Alert>
        )}

        {replenishment && !isPaymentManagedByB2B && isFullyPaid && (
          <Alert severity="success" sx={{ mb: 2 }}>Esta reposición ya está pagada por completo.</Alert>
        )}

        {canRegisterPayment && (
          <>
            <Typography variant="subtitle2">Nuevo pago</Typography>
            <RadioGroup value={paymentType} onChange={(e) => setPaymentType(e.target.value)} sx={{ mb: 1 }}>
              <FormControlLabel
                value="TOTAL"
                control={<Radio size="small" />}
                label={`Pago total: salda lo que resta (${formatMoney(remaining, replenishment.currency)})`}
              />
              <FormControlLabel
                value="PARTIAL"
                control={<Radio size="small" />}
                label="Pago parcial: pago una parte ahora"
              />
            </RadioGroup>
            {isPartialPayment && (
              <TextField
                label="Monto de este pago"
                type="number"
                value={partialAmount}
                onChange={(e) => setPartialAmount(e.target.value)}
                error={Boolean(partialAmountError)}
                helperText={partialAmountError || undefined}
                inputProps={{ min: 0, step: '0.01' }}
                fullWidth
                margin="dense"
              />
            )}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ flex: 1, minWidth: 160 }} margin="dense">
                <InputLabel id="payment-method-label">Forma de pago</InputLabel>
                <Select
                  labelId="payment-method-label"
                  value={paymentMethod}
                  label="Forma de pago"
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Fecha del pago"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1, minWidth: 160 }}
                margin="dense"
              />
            </Box>
          </>
        )}

        {historyRows.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Pagos realizados</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Forma de pago</TableCell>
                  <TableCell align="right">Importe</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyRows.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                    <TableCell>{PAYMENT_METHOD_LABELS[payment.paymentMethod] || '-'}</TableCell>
                    <TableCell align="right">{formatMoney(payment.amount, replenishment.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2">Factura</Typography>
        {invoiceUrl ? (
          <>
            <Link href={invoiceUrl} target="_blank" rel="noopener">{t('view_invoice')}</Link>
            <IconButton onClick={markInvoiceForDeletion} aria-label="delete invoice">{getIcon('trash')}</IconButton>
          </>
        ) : (
          <TextField
            type="file"
            onChange={handleFileChange}
            fullWidth
            margin="dense"
          />
        )}
      </DialogContent>
      <DialogActions>
        {canCancelReplenishment ? (
          <Button onClick={handleCancelReplenishment} color="error">
            {t('cancel_replenishment')}
          </Button>
        ) : null}
        <Button onClick={onClose}>{t('cancel')}</Button>
        <LoadingButton loading={loading} onClick={saveChanges} variant="contained" color="primary" disabled={!canSave}>
          {registeringPayment ? 'Registrar pago' : t('save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditReplenishmentModal;
